import {
    Directive,
    HostListener,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
    inject
} from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { FormControl } from '@angular/forms';

import { GenericValue } from '../model/generic-value';
import { HisBaseGlobalsService } from '../service/base/his-base-globals.service';
import { HisPlamComponent } from '../../structure/component/plam/his-plam.component';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'p-autoComplete[multiple], p-autocomplete[multiple]',
    standalone: false
})
export class HisChipsDirective implements OnChanges {
    private autoComplete = inject(AutoComplete);
    private parentPlamComponent = inject(HisPlamComponent);
    private hisBaseGlobalsService = inject(HisBaseGlobalsService);

    @Input('ngModel') chips: any[] = [];
    @Output() ngModelChange: EventEmitter<GenericValue[]> = new EventEmitter<GenericValue[]>();

    @Input() formControl: FormControl | null = null;
    @Input('hisDdKey') hisDdKey: string;
    @Input() addOnSeparators: string[] = [',', ';'];

    private readonly COMMA = ',';

    ngOnChanges(changes: SimpleChanges): void {
        const source = this.chips?.length
            ? this.chips
            : Array.isArray(this.formControl?.value)
                ? this.formControl?.value
                : [];

        if (source && this.autoComplete) {
            const converted = source.map(item => {
                if (typeof item === 'string') {
                    return new GenericValue(null, null, null, null, item, null);
                } else {
                    return item;
                }
            });
            const isChanged = JSON.stringify(this.chips) !== JSON.stringify(converted);

            if (isChanged) {
                if(this.chips){
                    this.chips.length = 0;
                    this.chips.push(...converted);
                }

                this.autoComplete.onModelChange(this.chips);
                this.ngModelChange.emit(this.chips);

                if (this.formControl) {
                    this.formControl.setValue(this.chips, { emitEvent: false });
                }
            }
        }

        if (this.parentPlamComponent && (this.hisDdKey && !this.hisBaseGlobalsService.getDd(this.hisDdKey)?.helptextIdentifier)) {
            this.parentPlamComponent.helptext = 'cs.sys.common.chips.component.helptext';
        }
    }

    @HostListener('paste', ['$event'])
    onExtendedPaste(event: ClipboardEvent): void {
        if (!this.addOnSeparators?.length) return;

        let pastedText = event.clipboardData?.getData('text') ?? '';
        this.addOnSeparators.forEach((sep) => {
            pastedText = pastedText.split(sep).join(this.COMMA);
        });

        pastedText.split(this.COMMA).forEach((value) => {
            this.addValue(value.trim());
        });

        event.preventDefault();
    }

    @HostListener( 'keydown', ['$event'] )
    onExtendedKeyDown( event: KeyboardEvent ): void {
        const inputEl = this.autoComplete.inputEL?.nativeElement;
        const inputValue = inputEl?.value?.trim();

        if (!inputEl || !inputValue) return;

        if (this.addOnSeparators.includes(event.key) || event.code === 'Enter' || event.code === 'NumpadEnter') {
            this.addValue(inputValue);
            inputEl.value = '';
            event.preventDefault();
        }
    }

    private addValue(value: string): void {
        if (!value) return;

        if (!this.chips) {
            this.chips = [];
        }

        const exists = this.chips.some(chip => chip.defaulttext === value);
        if (!exists) {
            const newChip = new GenericValue(null, null, null, null, value, null);
            this.chips.push(newChip);
            this.ngModelChange.emit(this.chips);

            if (this.formControl) {
                this.formControl.setValue(this.chips, { emitEvent: true });
            }
        }
    }
}
